import React from 'react';
import { Link } from 'react-router';

/*
  Everything `RawButton` hands down, which is everything Semantic's `Button` builds plus the
  handlers a `Popup` puts on its trigger. `disabled` arrives too and is left off on purpose: an
  anchor has no such attribute, and the disabled look rides in on the class name.
*/
export type RawButtonLinkOrAnchorProps = {
  children?: React.ReactNode;
  className?: string;
  'data-test'?: string;
  download?: string;
  id?: string;
  linkTo: string;
  onBlur?: React.FocusEventHandler<HTMLAnchorElement>;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLAnchorElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  onPointerDown?: React.PointerEventHandler<HTMLAnchorElement>;
  role?: string;
  tabIndex?: number;
  toTab?: string | null;
  type?: string;
};

/**
 * The anchor half of a `RawButton`, in its own file so it keeps one identity for the life of the
 * app: built inside `RawButton`'s body it would be a new component type every render, and React
 * would unmount and remount the anchor each time.
 */
export const RawButtonLinkOrAnchor = React.forwardRef<
  HTMLAnchorElement,
  RawButtonLinkOrAnchorProps
>(
  (
    {
      children,
      className,
      'data-test': dataTest,
      download,
      id,
      linkTo,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onMouseEnter,
      onMouseLeave,
      onPointerDown,
      role,
      tabIndex,
      toTab,
      type,
    },
    ref
  ) => {
    const target = toTab ? '_blank' : undefined;
    const rel = toTab ? 'noopener noreferrer' : undefined;

    /**
     * We only want to use the Link component if the link sent in isn't external facing
     * that's why we conditionally pick between an <a tag and <Link
     *
     * Otherwise, if you try to do <Link to="http://google.com" /> React router
     * thinks you're trying to call a page in the router.
     */
    /*
      A download is always a plain anchor: the router would read a same-origin path as a route and
      navigate, and `download` is not an attribute it forwards anyway.
    */
    return download ||
      ['http', 'mailto'].some(value =>
        linkTo.toLowerCase().startsWith(value)
      ) ? (
      <a
        className={className}
        data-test={dataTest}
        download={download}
        href={linkTo}
        id={id}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDown}
        ref={ref}
        rel={rel}
        role={role}
        tabIndex={tabIndex}
        target={target}
        type={type}
      >
        {children}
      </a>
    ) : (
      <Link
        className={className}
        data-test={dataTest}
        id={id}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDown}
        ref={ref}
        rel={rel}
        role={role}
        tabIndex={tabIndex}
        target={target}
        to={linkTo}
        type={type}
      >
        {children}
      </Link>
    );
  }
);
RawButtonLinkOrAnchor.displayName = 'RawButtonLinkOrAnchor';
